/**
 * TEMPORARY per-company tag fallback — DELETE THIS FILE (1NOW-1556, step 2).
 *
 * Until 1NOW-1556 these five companies had their marketing tags hardcoded as
 * an `if (company_id === …)` chain inside `src/index.tsx`, because GTM
 * containers, Ads conversion tags and chat widgets had no config home. They
 * now do (`CompanyWebsiteDetail.google_analytics_id` / `google_ads_id` /
 * `chat_widget_script_url`, editable in FMS → Website Settings), but the
 * tenants haven't been backfilled yet.
 *
 * This map keeps their tags firing in the meantime and disables ITSELF per
 * company, per tag, the moment a value is entered in FMS — see
 * `withLegacyFallback` below. So there is no window where a tenant is
 * double-counted, and none where they go dark.
 *
 * ---------------------------------------------------------------------------
 * TO REMOVE (the second, zero-risk half of the two-step rollout):
 *   1. Confirm all five companies below have their values saved in FMS and
 *      firing (Tag Assistant / GA realtime).
 *   2. Delete this file and the `withLegacyFallback` call in data-provider.
 * ---------------------------------------------------------------------------
 *
 * Note on GTM: the two GTM tenants are carried as plain gtag ids, because
 * gtag.js serves G-, AW- and GTM- from the same `?id=` endpoint. The canonical
 * GTM install also ships a <noscript> iframe, which the old chain reproduced;
 * it isn't reproduced here because this site is a client-rendered SPA — a
 * visitor without JavaScript never renders the page, so that fallback could
 * never have fired.
 */

import { MarketingTags } from "./marketingTags";

const LEGACY_TENANT_TAGS: Record<number, MarketingTags> = {
  589: {
    googleTagId: "G-0SNJCC3TQ1",
    customScripts: [
      {
        id: "legacy-chat",
        name: "Nextiva / Simplify360 chat",
        code: '<script src="https://d4cq8fw7kph8i.cloudfront.net/Simplify360Chat.js?key=Zlp0R0lXbCsySTQ9" async></script>',
        enabled: true,
      },
    ],
  },
  601: { googleTagId: "G-XBF1PE4573" },
  812: { googleTagId: "GTM-N9TSXTD8", googleAdsId: "AW-17574660149" },
  867: { googleTagId: "AW-17866244484" },
  1359: { googleTagId: "GTM-58FDQKPW" },
};

const isBlank = (v?: string | null) =>
  typeof v !== "string" || v.trim() === "";

/**
 * Fill in only the tags this tenant hasn't configured yet. Any value set in
 * FMS wins outright, so backfilling a company silently retires its entry here.
 */
export const withLegacyFallback = (
  companyId: number | undefined,
  configured: MarketingTags
): MarketingTags => {
  const legacy = companyId ? LEGACY_TENANT_TAGS[companyId] : undefined;
  if (!legacy) return configured;
  const hasConfiguredScripts = (configured.customScripts || []).some((s) =>
    !isBlank(s?.code)
  );
  return {
    googleTagId: isBlank(configured.googleTagId)
      ? legacy.googleTagId
      : configured.googleTagId,
    googleAdsId: isBlank(configured.googleAdsId)
      ? legacy.googleAdsId
      : configured.googleAdsId,
    customScripts: hasConfiguredScripts
      ? configured.customScripts
      : legacy.customScripts,
  };
};
